import { useEffect, useState } from 'react';

import Typography from '@componentLibrary/Typography';

import Form from '@components/form/Form/Form';
import PageLoading from '@components/layout/PageLoading';
import Paper from '@components/layout/Paper';
import CodeVerifiedOverlay from '@components/overlays/CodeVerifiedOverlay';
import { useQueryParams } from '@components/router';

import { useApp, useCodeAuth } from '@hooks/AppHooks';
import { useCountdown } from '@hooks/UIHooks';

import { sendSmsAuth } from '@helpers/auth';
import { isNativeApp } from '@helpers/isNativeApp';

import SMSConfirmationModal from '../SMSConfirmationModal';

type TPageParams = {
    code?: string;
    phone?: string;
    action?: string;
    // An additional string to use as a query param for the forwarding action.
    actionParams?: string;
};

const DOMAIN_TO_SCHEME: Record<string, string> = {
    'my.sparkplug.app': 'sparkplug',
    'sparkplug.app': 'sparkplug',
    'demo.sparkplug-technology.io': 'sparkplug-demo',
    'staging.sparkplug-technology.io': 'sparkplug-stg',
    'dev.sparkplug-technology.io': 'sparkplug-dev',
};

function getNativeScheme(): string | null {
    return DOMAIN_TO_SCHEME[window.location.hostname] ?? null;
}

function isMobileDevice(): boolean {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * On mobile browsers, attempt to hand off to the native app via a custom URL
 * scheme before consuming the auth code on the web. Returns `true` while the
 * handoff attempt is in progress so callers can defer web-side auth.
 */
function useNativeAppHandoff(hasCode: boolean): boolean {
    const shouldAttempt = !isNativeApp() && isMobileDevice() && hasCode && getNativeScheme() != null;

    const [pending, setPending] = useState(shouldAttempt);

    useEffect(() => {
        if (!pending) return;

        const scheme = getNativeScheme();
        if (!scheme) {
            setPending(false);
            return;
        }

        window.location.href = `${scheme}://a${window.location.search}`;

        const timer = setTimeout(() => setPending(false), 1500);
        return () => clearTimeout(timer);
    }, [pending]);

    return pending;
}

const ExpiredLinkCard = ({ phoneNumber }: { phoneNumber: string }) => {
    const [showSMSConfirmationModal, setShowSMSConfirmationModal] = useState<boolean>(false);
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { history, user } = useApp();

    const { remainingTime, startCountdown, countdownIsRunning } = useCountdown();
    const canSendCode = !countdownIsRunning;

    useEffect(() => {
        if (canSendCode) {
            setShowWarning(false);
        }
    }, [canSendCode]);

    if (user?._id != null) {
        history.push(`/user/sparks`);
    }

    const sendSMSCode = () => {
        if (!canSendCode) {
            setShowWarning(true);
            return;
        }

        startCountdown(30); // 30 seconds
        setErrorMessage('');
        sendSmsAuth(phoneNumber)
            .then((didSend) => {
                if (didSend) {
                    setShowSMSConfirmationModal(true);
                }
            })
            .catch((err) => {
                if (err?.message != null) {
                    setErrorMessage(err?.message);
                } else {
                    setErrorMessage('An unknown error occured');
                }
            });
    };

    return (
        <>
            <Paper className="flex flex-col items-center gap-8">
                <div className="flex flex-col gap-2 items-center mt-2">
                    <Typography variant="xl" className="font-bold">
                        Sign back in
                    </Typography>
                    <div className="flex flex-col">
                        <Typography variant="base" className="text-gray-800 text-center">
                            Looks like that login link has expired.
                        </Typography>
                    </div>
                </div>

                {errorMessage && <p className="text-sienna-400">{errorMessage}</p>}

                {showWarning ? (
                    <p>{` You may request another code in ${remainingTime} seconds.`}</p>
                ) : (
                    <div className="flex flex-col w-full">
                        <Form className="flex flex-col gap-4">
                            <Form.PhoneField
                                label="Mobile number"
                                name="phoneNumber"
                                required
                                autoComplete="on"
                                defaultValue={phoneNumber}
                                disabled
                                className="w-full gap-1"
                            />
                            <div className="flex flex-col w-full gap-3 items-center justify-center">
                                <Form.Button
                                    color="blue"
                                    variant="smooth"
                                    onClick={sendSMSCode}
                                    className="w-full"
                                >
                                    Text me a new login code
                                </Form.Button>
                                <Form.Button
                                    color="white"
                                    variant="smooth"
                                    onClick={() => {
                                        history.push('/employee/login');
                                    }}
                                    className="w-full !text-gray-800"
                                >
                                    Not you? Change number
                                </Form.Button>
                            </div>
                        </Form>
                    </div>
                )}
            </Paper>
            {showSMSConfirmationModal && (
                // fully dismount the modal on close to reset states
                <SMSConfirmationModal
                    isVisible
                    phoneNumber={phoneNumber}
                    onClose={() => setShowSMSConfirmationModal(false)}
                />
            )}
        </>
    );
};

const AuthCodeView = () => {
    const { code, phone, action, actionParams }: TPageParams = useQueryParams();
    const phoneNumber = (phone || '').replace(' ', '+');

    const handoffPending = useNativeAppHandoff(!!code);

    const { didInitialCodeSubmit, codeIsValid } = useCodeAuth(
        {
            code: handoffPending ? undefined : code,
            phoneNumber,
        },
        6,
        false,
    );

    let redirectTo = '/user/sparks';

    if (action === 'claim') {
        redirectTo = '/user/wallet';
    }

    if (action === 'withdrawal') {
        redirectTo = `/user/wallet?withdrawalId=${actionParams}`;
    }

    if (action === 'help') {
        redirectTo = '/user/sparks?openIntercom=true';
    }

    if (action === 'courses') {
        redirectTo = '/user/sparks?tab=learn-and-earn';
    }

    return (
        <div className="auth-code-view">
            {didInitialCodeSubmit && !codeIsValid ? (
                <ExpiredLinkCard phoneNumber={phoneNumber} />
            ) : (
                <PageLoading label="Authorizing..." />
            )}
            <CodeVerifiedOverlay isVisible={codeIsValid} redirectTo={redirectTo} />
        </div>
    );
};

export default AuthCodeView;
